/**
 * Categories, per kind of space.
 *
 * A fixed list in code, not a table with a management screen — CLAUDE.md puts
 * "category management UI" explicitly out of scope, and for one household and
 * one restaurant a settings screen for editing categories would cost more
 * attention than it ever returns. Adding one here is a one-line change.
 *
 * The list is deliberately short. Categories exist to answer "where does the
 * money go", and twenty of them answer it worse than eight.
 *
 * `Lissabon` sits oddly on that list, and it is worth being honest about why.
 * Every other entry names a *kind* of cost; this one names a *place*. It works
 * — you can tag, filter and search on it today — but it will not give you a
 * monthly total for Portugal separately from the household, because the totals
 * are computed per space, not per category.
 *
 * If that separate total is what is wanted, the right shape is a third space
 * beside Huis and Zaak: spaces already carry their own totals, their own
 * filter, their own lead time and their own green-lane ceiling. That is a
 * bigger change than a line in this list, so it is not made silently.
 */

export type SpaceKind = 'household' | 'business'

export const CATEGORIES: Record<SpaceKind, readonly string[]> = {
  household: [
    'Energie',
    'Water',
    'Telecom',
    'Verzekering',
    'Wonen',
    'Gezondheid',
    'Belastingen',
    // A place, not a kind of cost — the odd one out on this list, and
    // deliberately so. See the note under CATEGORIES.
    'Lissabon',
    'Overig',
  ],
  business: [
    'Voeding & dranken',
    'Energie',
    'Huur & pand',
    'Verzekering',
    'Personeel',
    'Onderhoud',
    'Belastingen & BTW',
    'Overig',
  ],
} as const

export function categoriesFor(kind: SpaceKind): readonly string[] {
  return CATEGORIES[kind] ?? CATEGORIES.household
}

/**
 * A first guess for a supplier nobody has categorised yet.
 *
 * Keyword matching, not a language model: these are the names of Belgian
 * utilities and the words that appear on their invoices, and a list of strings
 * is both cheaper and more predictable than asking a model to pick from eight
 * options. Wrong guesses cost one tap to fix, and the fix is remembered.
 */
const HINTS: { pattern: RegExp; household: string; business: string }[] = [
  { pattern: /engie|luminus|eneco|mega|octa\+|elektricit|electric|gas\b/i, household: 'Energie', business: 'Energie' },
  { pattern: /water|farys|pidpa|vivaqua|de watergroep/i, household: 'Water', business: 'Energie' },
  { pattern: /proximus|telenet|orange|base|scarlet|voo|internet|telefoon/i, household: 'Telecom', business: 'Overig' },
  { pattern: /verzeker|assur|insurance|ag insurance|kbc verzeker|axa|ethias|baloise/i, household: 'Verzekering', business: 'Verzekering' },
  { pattern: /huur|rent|syndic|immo/i, household: 'Wonen', business: 'Huur & pand' },
  { pattern: /mutualiteit|ziekenfonds|apothe|dokter|tandarts|hospita/i, household: 'Gezondheid', business: 'Overig' },
  { pattern: /belasting|fod financ|btw|vat|gemeente|stad /i, household: 'Belastingen', business: 'Belastingen & BTW' },
  { pattern: /metro|sligro|colruyt|makro|bidfood|deli|brouwerij|drank|food/i, household: 'Overig', business: 'Voeding & dranken' },
  { pattern: /onderhoud|herstel|technic|installat|schoonmaak/i, household: 'Wonen', business: 'Onderhoud' },
  { pattern: /sodexo|securex|acerta|partena|loon|payroll/i, household: 'Overig', business: 'Personeel' },
]

export function guessCategory(supplierName: string | null, kind: SpaceKind): string | null {
  if (!supplierName) return null
  for (const hint of HINTS) {
    if (hint.pattern.test(supplierName)) return kind === 'business' ? hint.business : hint.household
  }
  return null
}

/** "Restaurant" / "Thuis" is what a person says; household/business is our word. */
export function describeSpaceKind(kind: SpaceKind): string {
  return kind === 'business' ? 'Zaak' : 'Huis'
}
